import React from 'react';
import {Link} from 'react-router';
import {useSettings} from '@ui/settings/use-settings';
import {useThemeSelector} from '@ui/themes/theme-selector-context';

export function IccdbFooter() {
  const {branding} = useSettings();
  const {selectedTheme} = useThemeSelector();
  const isDarkMode = selectedTheme?.is_dark ?? true;

  const siteName = branding?.site_name || 'ICCDB';
  const siteDescription = branding?.site_description || 'International Central Cinema Database. The authoritative destination for discovering movies, actors, actresses, directors, and filmography.';

  // Logo based on theme
  const footerLogo = isDarkMode
    ? (branding?.logo_light || branding?.logo_dark)
    : (branding?.logo_dark || branding?.logo_light);

  const footerBg = isDarkMode ? '#020617' : '#f8fafc';
  const footerBorder = isDarkMode ? '#1e293b' : '#e2e8f0';
  const textMuted = isDarkMode ? '#94a3b8' : '#64748b';
  const headingText = isDarkMode ? '#f8fafc' : '#0f172a';

  return (
    <footer
      style={{
        backgroundColor: footerBg,
        borderTop: `1px solid ${footerBorder}`,
        color: textMuted,
        padding: 'clamp(36px, 5vw, 64px) clamp(16px, 4vw, 32px) clamp(28px, 4vw, 48px)',
        transition: 'background-color 0.2s, border-color 0.2s',
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
      }}
      className="text-xs sm:text-sm font-sans"
    >
      <div className="container mx-auto max-w-7xl">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-8 mb-10">
          {/* Brand Info */}
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2 mb-3">
              {footerLogo ? (
                <img
                  src={footerLogo}
                  alt={siteName}
                  style={{
                    height: '32px',
                    maxHeight: '36px',
                    width: 'auto',
                    maxWidth: '160px',
                    objectFit: 'contain',
                    display: 'block',
                  }}
                />
              ) : (
                <>
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-amber-500 to-rose-600 flex items-center justify-center font-black text-white text-base shadow-sm">
                    {siteName.slice(0, 2).toUpperCase()}
                  </div>
                  <span
                    style={{color: headingText}}
                    className="font-bold text-lg tracking-tight"
                  >
                    {siteName}
                  </span>
                </>
              )}
            </div>
            <p className="text-xs leading-relaxed max-w-sm" style={{color: textMuted}}>
              {siteDescription}
            </p>
          </div>

          {/* Discovery */}
          <div className="col-span-1">
            <h4
              style={{color: headingText}}
              className="font-bold mb-3 uppercase tracking-wider text-xs flex items-center gap-1.5"
            >
              <i className="fa-solid fa-compass text-amber-500 text-[11px]"></i>
              Discovery
            </h4>
            <ul className="space-y-2 text-xs">
              <li>
                <Link to="/movies" className="hover:text-amber-500 transition-colors">
                  All Movies
                </Link>
              </li>
              <li>
                <Link to="/series" className="hover:text-amber-500 transition-colors">
                  TV & Web Series
                </Link>
              </li>
              <li>
                <Link to="/people" className="hover:text-amber-500 transition-colors">
                  Stars & Talent
                </Link>
              </li>
              <li>
                <Link to="/news" className="hover:text-amber-500 transition-colors">
                  Cinema News
                </Link>
              </li>
              <li>
                <Link to="/movies?sort=recent" className="hover:text-amber-500 transition-colors">
                  Recently Approved
                </Link>
              </li>
            </ul>
          </div>

          {/* Portals */}
          <div className="col-span-1">
            <h4
              style={{color: headingText}}
              className="font-bold mb-3 uppercase tracking-wider text-xs flex items-center gap-1.5"
            >
              <i className="fa-solid fa-layer-group text-amber-500 text-[11px]"></i>
              Portals
            </h4>
            <ul className="space-y-2 text-xs">
              <li>
                <Link to="/director" className="text-amber-500 hover:text-amber-400 font-semibold transition-colors flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                  Director Portal
                </Link>
              </li>
              <li>
                <Link to="/admin" className="hover:text-amber-500 transition-colors">
                  Admin Approval
                </Link>
              </li>
              <li>
                <Link to="/login" className="hover:text-amber-500 transition-colors">
                  Sign In
                </Link>
              </li>
              <li>
                <Link to="/register" className="hover:text-amber-500 transition-colors">
                  Create Account
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal & Trust */}
          <div className="col-span-2 md:col-span-1 pt-4 md:pt-0 border-t md:border-t-0 border-slate-800/40">
            <h4
              style={{color: headingText}}
              className="font-bold mb-3 uppercase tracking-wider text-xs flex items-center gap-1.5"
            >
              <i className="fa-solid fa-shield-halved text-amber-500 text-[11px]"></i>
              Legal & Trust
            </h4>
            <ul className="flex flex-wrap md:flex-col gap-x-4 gap-y-2 text-xs">
              <li>
                <Link to="/contact" className="hover:text-amber-500 transition-colors">
                  Contact Support
                </Link>
              </li>
              <li>
                <span className="opacity-70">Privacy Policy</span>
              </li>
              <li>
                <span className="opacity-70">Terms of Service</span>
              </li>
              <li>
                <span className="opacity-70">Submission Guidelines</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div
          style={{borderTop: `1px solid ${footerBorder}`}}
          className="pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-center sm:text-left"
        >
          <p style={{color: textMuted, lineHeight: 1.6}}>
            © {new Date().getFullYear()} {siteName}. All rights reserved. Content submitted by verified directors and reviewed by {siteName} administration.
          </p>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="font-medium" style={{color: textMuted}}>
              Role-Based Movie Content Platform
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}

